import ScrollReveal from "@/components/base/ScrollReveal";
import { Link } from "react-router-dom";

export default function WhoWeHelpSection() {
  const audiences = [
    { icon: "ri-building-4-line", label: "Companies small to large" },
    { icon: "ri-government-line", label: "Federal/national government agencies" },
    { icon: "ri-map-pin-2-line", label: "State/province government agencies" },
    { icon: "ri-graduation-cap-line", label: "Academic and nonprofit institutions" },
    { icon: "ri-earth-line", label: "International policy entities" },
    { icon: "ri-user-star-line", label: "CAIO, CDAO, CHRO, other C-Suite Leaders, Directors, etc." },
    { icon: "ri-folders-line", label: "Departments within larger enterprises" },
    { icon: "ri-group-line", label: "Cross-functional AI planning teams" },
    { icon: "ri-briefcase-line", label: "Operations, IT, HR, governance, legal/compliance, and strategy teams involved in AI adoption" },
  ];

  // Row configs: each row has items + an indent multiplier (fraction of card+gap width)
  // Row 1: items 0-2, no indent
  // Row 2: items 3-5, indented by ~half card width to stagger
  // Row 3: items 6-8, no indent (same as row 1) — creates a zig-zag/brick pattern
  const rows = [
    { items: audiences.slice(0, 3), indent: 0 },
    { items: audiences.slice(3, 6), indent: 1 },
    { items: audiences.slice(6, 9), indent: 0 },
  ];

  return (
    <section id="who-we-help" className="relative bg-bg-section py-20 md:py-28 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-1/4 -right-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />
      <div className="absolute bottom-1/4 -left-32 w-[300px] h-[300px] bg-primary/4 rounded-full blur-[100px]" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="font-sans text-[14px] font-medium text-primary tracking-wider uppercase">
                Who We Serve
              </span>
              <h2 className="font-serif text-[32px] md:text-[44px] font-semibold text-text-headline leading-tight mt-4 mb-6">
                Who <span className="italic text-primary">VantageSight</span> Helps
              </h2>
              <p className="font-sans text-[17px] md:text-[19px] text-text-muted leading-relaxed max-w-3xl mx-auto">
                VantageSight works with organizations that want to adopt AI responsibly but need clearer direction, governance, readiness, and structure.
              </p>
            </div>
          </ScrollReveal>

          {/* Collaboration image */}
          <ScrollReveal delay={100}>
            <div className="mb-14 max-w-5xl mx-auto">
              <div className="relative group rounded-2xl overflow-hidden shadow-2xl bg-bg-card">
                <img
                  src="https://storage.readdy-site.link/project_files/d2cadc96-e8bf-4a0a-9bca-6276c5c079f1/f0a48c51-0a13-42b0-97c4-2c9b45c1a651_ChatGPT-Image-May-9-2026-09_42_44-AM.png?v=3c3a7f8cc6af0f5bcf0237ff23b3b1a1"
                  alt="Diverse team of professionals collaborating on AI governance and strategy in modern office setting"
                  className="w-full h-auto object-contain object-center"
                />
              </div>
            </div>
          </ScrollReveal>

          {/* Desktop: 3-row stagger layout (3+3+3) — alternating rows are offset so cards
              sit visually between the gaps of the row above, removing false column pairings */}
          <div className="hidden lg:block max-w-5xl mx-auto space-y-4">
            {rows.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className="flex gap-4"
                style={
                  row.indent
                    ? { paddingLeft: 'calc(16.67% + 0.34rem)', paddingRight: 'calc(16.67% + 0.34rem)' }
                    : {}
                }
              >
                {row.items.map((a, colIdx) => {
                  const delay = (rowIdx * 3 + colIdx) * 80;
                  return (
                    <div key={colIdx} className="flex-1">
                      <ScrollReveal delay={delay} direction="up" className="h-full">
                        <div className="bg-bg-card rounded-xl p-6 border border-border transition-all duration-300 hover:border-primary/40 hover:-translate-y-1 group h-full">
                          <div className="w-10 h-10 bg-primary-tint rounded-full flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-primary/15">
                            <i className={`${a.icon} text-primary text-lg w-5 h-5 flex items-center justify-center`} />
                          </div>
                          <p className="font-sans text-[15px] font-medium text-text-headline leading-snug">
                            {a.label}
                          </p>
                        </div>
                      </ScrollReveal>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Mobile / tablet fallback: 2-col grid */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {audiences.map((a, i) => (
              <ScrollReveal key={i} delay={i * 80} direction="up">
                <div className="bg-bg-card rounded-xl p-6 border border-border transition-all duration-300 hover:border-primary/40 hover:-translate-y-1 group h-full">
                  <div className="w-10 h-10 bg-primary-tint rounded-full flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-primary/15">
                    <i className={`${a.icon} text-primary text-lg w-5 h-5 flex items-center justify-center`} />
                  </div>
                  <p className="font-sans text-[15px] font-medium text-text-headline leading-snug">
                    {a.label}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={400}>
            <div className="text-center mt-14">
              <Link
                to="/contact"
                className="font-sans text-[16px] font-medium text-white bg-primary hover:bg-primary-light px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                Book an Introductory Call
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}